import React, { useState, useEffect } from "react";
import { Calendar, Clock, User, X, Plus } from 'lucide-react';

const RoomBooking = () => {

  const days = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    date: "",
    startTime: "08:30",
    endTime: "09:15",
  })

  const [data, setData] = useState([])
  const [editingId, setEditingId] = useState(null);
  
  useEffect(() => {
    fetch("/api/bookings.php")
      .then(res => res.json())
      .then(bookings => setData(bookings))
      .catch(err => {
        console.error("Erreur chargement bookings", err);
        setData([]);
      });
  }, []);

  const timeSlots = [];
  for (let h = 8; h < 22; h++) {
    ["00", "15", "30", "45"].forEach((m) =>
      timeSlots.push(`${String(h).padStart(2, "0")}:${m}`),
    );
  }
  timeSlots.push("22:00");

  const getMonday = (weekOffset = 0) => {
    const today = new Date();
    const day = today.getUTCDay();
    const diff = day === 0 ? -6 : 1 - day;

    const monday = new Date(Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate() + diff + weekOffset * 7
    ));

    return monday;
  };

  const getDateForDay = (i) => {
    const d = new Date(getMonday(selectedWeek));
    d.setDate(d.getDate() + i);
    return toLocalDate(d);
  };

  const getBookingsForDay = (date) =>
    Array.isArray(data)
      ? data
          .filter(b => toLocalDate(b.date) === date)
          .map(b => ({
            ...b,
            startTime: b.startTime.slice(0, 5),
            endTime: b.endTime.slice(0, 5),
          }))
          .sort((a, b) => a.startTime.localeCompare(b.startTime))
      : [];

  const getWeekRange = () => {
    const monday = getMonday(selectedWeek);
    const saturday = new Date(monday); saturday.setDate(saturday.getDate() + 5);
    return `${monday.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} - ${saturday.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}`; };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }); };

  const toLocalDate = (d) => {
    const date = new Date(d);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  async function handleSubmit(e) {
    e.preventDefault();

    // 1️⃣ Vérification heures
    if (formData.startTime >= formData.endTime) {
      alert("L'heure de fin doit être après l'heure de début");
      return;
    }

    // 2️⃣ Vérification conflits
    const hasConflict = () => {
      const date = formData.date;
      const start = formData.startTime;
      const end = formData.endTime;

      return data.some(b => {
        // En mode édition, on ignore la réservation en cours
        if (editingId && b.id === editingId) return false;

        if (toLocalDate(b.date) !== date) return false;

        const bStart = b.startTime.slice(0, 5);
        const bEnd = b.endTime.slice(0, 5);

        return start < bEnd && end > bStart;
      });
    };

    if (hasConflict()) {
      alert("⚠️ Ce créneau est déjà réservé");
      return;
    }

    // 3️⃣ Choix API (add ou edit)
    const url = editingId
      ? "/api/edit_booking.php"
      : "/api/add_booking.php";

    const payload = editingId
      ? { ...formData, id: editingId }
      : formData;

    try {
      // 4️⃣ Envoi vers PHP
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error("Erreur lors de l'enregistrement");
      }

      // 5️⃣ Recharge les réservations
      const bookingsRes = await fetch("/api/bookings.php");
      const bookings = await bookingsRes.json();
      setData(bookings);

      // 6️⃣ Reset formulaire
      setShowForm(false);
      setEditingId(null);
      setFormData({
        name: "",
        date: "",
        startTime: "08:30",
        endTime: "09:15",
      });

      console.log(editingId ? "Réservation modifiée" : "Réservation ajoutée");

    } catch (err) {
      console.error("Erreur handleSubmit", err);
      alert("Une erreur est survenue");
    }
  }

  function editBooking(booking) {
    setFormData({
      name: booking.name,
      date: toLocalDate(booking.date),
      startTime: booking.startTime.slice(0, 5),
      endTime: booking.endTime.slice(0, 5),
    });

    setEditingId(booking.id);
    setShowForm(true);

    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  
  async function deleteBooking(id) {
    if (!window.confirm("Supprimer cette réservation ?")) return;

    try {
      const res = await fetch("/api/delete_booking.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ id })
      });

      if (!res.ok) {
        throw new Error("Erreur suppression");
      }

      // Recharger les réservations
      const bookingsRes = await fetch("/api/bookings.php");
      const bookings = await bookingsRes.json();
      setData(bookings);

    } catch (err) {
      console.error("Erreur deleteBooking", err);
    }
  }

  return (
    <div className="min-vh-100 bg-light p-4">
      <div className="container-xl">
        <div className="bg-white rounded shadow p-4 mb-4">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h1 className="h3 fw-bold text-dark d-flex align-items-center gap-2">
                <Calendar className="text-primary" />
                Réservation Salle "Brel"
              </h1>
              <p className="text-secondary mb-0 gap-2">
                8h30 - 21h30 • Lundi - Samedi
              </p>
              <button
                onClick={() => {
                  setShowForm(!showForm);
                  if (showForm) {
                    setEditingId(null);
                    setFormData({
                      name: "",
                      date: "",
                      startTime: "08:30",
                      endTime: "09:15",
                    });
                  }
                }}
                className="btn btn-primary d-flex align-items-center gap-2 mt-2"
              >
                <Plus size={20} />
                Nouvelle réservation
              </button>
              {showForm && (
                <div className="bg-primary bg-opacity-10 p-3 rounded mt-3">
                  <h3 className="fw-semibold mb-3">Nouvelle Réservation</h3>
                  <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-medium" htmlFor='name'>Nom</label>
                      <input className="form-control" type='text' name='name' required onChange={(e)=> setFormData({...formData, name: e.target.value})} />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-medium" htmlFor='date'>Date</label>
                      <input className="form-control" type='date' name='date' required onChange={(e)=> setFormData({...formData, date: e.target.value})} />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-medium" htmlFor='startTime'>Heure de début</label>
                      <select className="form-select" name='startTime' required onChange={(e)=> setFormData({...formData, startTime: e.target.value})}>
                        {timeSlots.slice(0, -1).map((time) => (
                        <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-medium" htmlFor='endTime'>Heure de fin</label>
                      <select className="form-select" name='endTime' required onChange={(e)=> setFormData({...formData, endTime: e.target.value})}>
                        {timeSlots.slice(1).map((time) => (
                        <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="d-flex gap-2 mt-3">
                    <button type="submit" className="btn btn-primary">
                      {editingId ? "Modifier" : "Enregistrer"}
                    </button>
                    <button
                      onClick={() => {
                        setShowForm(false);
                        setEditingId(null);
                        setFormData({
                          name: "",
                          date: "",
                          startTime: "08:30",
                          endTime: "09:15",
                        });
                      }}
                      className="btn btn-secondary"
                    >
                      Annuler
                    </button>
                  </div>
                  </form>
                </div>
                )}
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-between mb-3 mt-3">
            <button
              onClick={() => setSelectedWeek(selectedWeek - 1)}
              className="btn btn-outline-secondary"
            >
              ← Semaine précédente
            </button>

            <span className="fw-semibold text-secondary">
              {getWeekRange()}
            </span>

            <button
              onClick={() => setSelectedWeek(selectedWeek + 1)}
              className="btn btn-outline-secondary"
            >
              Semaine suivante →
            </button>
          </div>
        </div>
        <div className="row g-3">
          {days.map((day, i) => {
            const date = getDateForDay(i);
            const dayBookings = getBookingsForDay(date);

            return (
              <div className="col-md-4 mb-3" key={day}>
                <div className="bg-white rounded shadow p-3 h-100">
                  <div className="border-bottom pb-2 mb-2">
                    <h2 className="h5 fw-bold">{day}</h2>
                    <p className="text-muted small mb-0">{formatDate(date)}</p>
                  </div>

                  {dayBookings.length === 0 ? (
                    <p className="text-muted fst-italic small">
                      Aucune réservation
                    </p>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      {dayBookings.map((booking) => (
                        <div
                          key={booking.id}
                          className={`border-start border-4 p-2 rounded ${
                          booking.name === "Cours"
                            ? "bg-danger bg-opacity-25 border-danger"
                            : "bg-primary bg-opacity-10 border-primary"
                        }`}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center gap-2 fw-semibold small">
                                <User size={16} />
                                {booking.name}
                              </div>
                              <div className="d-flex align-items-center gap-2 text-muted small mt-1">
                                <Clock size={14} />
                                {booking.startTime} - {booking.endTime}
                              </div>
                            </div>
                            <div className="d-flex gap-1">
                              <button
                                onClick={() => editBooking(booking)}
                                className="btn btn-sm btn-outline-dark"
                                title="Modifier"
                              >
                                Modifier
                              </button>
                              <button
                                onClick={() => deleteBooking(booking.id)}
                                className="btn btn-sm btn-outline-dark"
                                title="Supprimer"
                              >
                                <X size={18} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
)};

export default RoomBooking;
